import { Location, NavigateFunction } from 'react-router-dom';

import { getStorageItem, updateStorageItem } from 'utils/browserCache';

import { ConversationStateType, ConversationReducerActionType } from 'hooks/conversation/types';
import { ChatInitializeViewType } from 'utils/browserCache/types';
import { ViewStateType } from 'hooks/view/types';

import { STORAGE_LOCATION, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from 'constants/browserCache';
import { ACTIONS } from 'constants/hook';
import { SEARCH_PARAMS } from 'constants/url';
import { ROUTES } from 'routes/constants';

/**
 * Used to initialize chat window with correct open/close state.
 * If chat should be open, then also make sure chat is on the correct route/view.
 * @param currInitializeViewState
 * @param navigate
 * @param conversationState
 * @param dispatchConversationAction
 * @param isExistingConversation
 */
export const initialChatNavigate = (
  currInitializeViewState: ChatInitializeViewType,
  navigate: NavigateFunction,
  conversationState: ConversationStateType,
  dispatchConversationAction: (action: ConversationReducerActionType) => void,
  isExistingConversation: boolean,
) => {
  const route = isExistingConversation ? ROUTES.CHAT : currInitializeViewState?.route;
  if (currInitializeViewState?.route || isExistingConversation) {
    if (route === ROUTES.CHAT) {
      const needsInitialization =
        !conversationState.isConnected && !conversationState.isInitializationRequested;

      if (needsInitialization) {
        dispatchConversationAction({
          type: ACTIONS.CHANGE,
          payload: { isInitializationRequested: true },
        });
      }
    }
    navigate(route);
  }
};

/**
 * Used to initialize Chat's question input field.
 */
export const initialChatQuestionInput = (
  dispatchConversationAction: (action: ConversationReducerActionType) => void,
) => {
  const currInitializeViewState = getStorageItem(
    STORAGE_LOCATION.SESSION_STORAGE,
    LOCAL_STORAGE_KEY.CHAT_INITIALIZE_VIEW,
  ) as ChatInitializeViewType;

  if (currInitializeViewState?.inputText) {
    dispatchConversationAction({
      type: ACTIONS.CHANGE,
      payload: {
        questionInputText: currInitializeViewState.inputText,
      },
    });
  }
};
/**
 * Determine if a URL param was set to initialize Chat window to be open.
 * @returns If a URL param was set to initialize Chat window to be open.
 */
export const getChatOpenUrlParam = () => {
  // FIXME - some incoming URLs may be formatted incorrectly.
  // Account for them. See https://carvana.slack.com/archives/C054N7KJDQV/p1687818564036199?thread_ts=1687477964.673569&cid=C054N7KJDQV
  const searchStr = window.location.search;
  const searchParams = new URLSearchParams(searchStr);
  const openChatParam = searchParams.get(SEARCH_PARAMS.OPEN_CHAT);
  const isOpenChatUrl = openChatParam === 'true' || searchStr.includes('openChat=true');
  return isOpenChatUrl;
};

/**
 * Determine if a URL param was set to initialize Chat window to be open.
 * @returns If a URL param was set to initialize Chat window to be open.
 */
export const getEscalationUrlParam = () => {
  // FIXME - some incoming URLs may be formatted incorrectly.
  // Account for them. See https://carvana.slack.com/archives/C054N7KJDQV/p1687818564036199?thread_ts=1687477964.673569&cid=C054N7KJDQV
  const searchStr = window.location.search;
  const searchParams = new URLSearchParams(searchStr);
  const escalateChatParam = searchParams.get(SEARCH_PARAMS.ESCALATE_CHAT);

  return escalateChatParam === 'true' || searchStr.includes(`${SEARCH_PARAMS.ESCALATE_CHAT}=true`);
};

/**
 * Used to store the user's current Chat Route.
 * This value is used to navigate the user to correct Route on page reloads and Chat initializes as open.
 * @param initializing Flag indicating if the ChatApp is still mounting.
 * @param location Location user would see if Chat was initiazed open.
 * @returns
 */
export const trackChatRouteChanges = (initializing: boolean, location: Location) => {
  // Need this check, otherwise route will be overridden to '/'
  if (initializing) return;

  const currInitializeViewState = getStorageItem(
    STORAGE_LOCATION.SESSION_STORAGE,
    SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW,
  ) as ChatInitializeViewType;

  updateStorageItem(STORAGE_LOCATION.SESSION_STORAGE, SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW, {
    ...currInitializeViewState,
    wasInitOpen: currInitializeViewState?.open || false,
    route: location.pathname,
  });
};

/**
 * Used to store the current open/closed state of Chat.
 * These values are used on new page loads to determine if Chat should initialize open.
 * @param initializing Flag indicating if the ChatApp is still mounting.
 * @param viewState View State that stores the current open/close state of Chat.
 * @returns
 */
export const trackChatOpenClose = (initializing: boolean, viewState: ViewStateType) => {
  if (initializing) return;

  const currInitializeViewState = getStorageItem(
    STORAGE_LOCATION.SESSION_STORAGE,
    SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW,
  ) as ChatInitializeViewType;

  updateStorageItem(STORAGE_LOCATION.SESSION_STORAGE, SESSION_STORAGE_KEY.CHAT_INITIALIZE_VIEW, {
    ...currInitializeViewState,
    open: viewState.isPanelOpen,
  });
};
